import React, { useRef, useState, useEffect } from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import { isEmpty } from '@sainsburys-tech/boltui-utils'
import { Button, Drawer, Tabs, Tab, Image, ProductPanel } from '@sainsburys-tech/bolt'
import { onQuickViewSlidingPanelOpen } from './ugc.tracking'
import { fetcher } from '../../../helpers/ComponentHelper'
import PLPPanel from './PLPPanel'
import * as Styled from './ugc.styles'

const UGCQuickView = (props) => {
  const initialQuickviewRender = useRef(true)
  const imageRef = useRef(null)
  const tabsRef = useRef()
  const [activeTab, setActiveTab] = useState(0)
  const [isLoading, setLoading] = useState(true)
  const [productsData, setProducts] = useState({ products: [], included: [] })
  const { displayImage, galleryImages, toggleCallback, updateDrawerInformation, galleryImagesLimit, productTab } = props

  const goToTab = (event, tabIndex) => {
    // eslint-disable-next-line no-unused-expressions
    event && event.preventDefault()
    setActiveTab(tabIndex)
  }

  const resolveProductDetails = (productResults) => {
    if (productResults.data) {
      const { data, included } = productResults
      const productObject = {
        products: Array.isArray(data) ? data : [data],
        included: Array.isArray(included) ? included : [included]
      }
      return ReactDOM.unstable_batchedUpdates(() => {
        setProducts(productObject)
        setLoading(false)
        onQuickViewSlidingPanelOpen(
          Array.isArray(data) ? data : [data],
          Array.isArray(included) ? included : [included],
          {
            type: 'socialQuickview',
            currentTab: 'iWantToShop',
            clickOrigin: 'ugcComponent',
            eventName: 'quickviewLbPage'
          },
          window
        )
      })
    }
    return null
  }

  const fetchProductData = async (productIds) => {
    try {
      const partNumbers = productIds?.join(',')
      const options = {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/vnd.argos-product.v2+json' },
        credentials: 'include'
      }
      const url = `/product-api/pdp-service/partNumber/${partNumbers}?returnMeta=true`
      const { json, status, ok } = await fetcher(url, options)
      if (!ok) {
        throw new Error(`There has been an error with (${status})`)
      }
      return resolveProductDetails(json)
    } catch (err) {
      console.error(err)
      return ReactDOM.unstable_batchedUpdates(() => {
        setProducts({ products: [], included: [] })
        setLoading(false)
      })
    }
  }

  const productPanelOnClick = (productId) => {
    window.location.href = `/product/${productId}`
  }

  useEffect(() => {
    if (initialQuickviewRender.current) {
      initialQuickviewRender.current = false
    } else if (!isEmpty(displayImage?.productIds)) {
      fetchProductData(displayImage?.productIds)
    } else {
      setProducts({ products: [], included: [] })
    }
  }, [displayImage])

  const displayError = isEmpty(productsData.products) && isEmpty(displayImage?.nonPDPs)

  return (
    <>
      <Drawer
        heading={`Shared by @${displayImage?.author}`}
        show={!isEmpty(displayImage)}
        padding={false}
        onClose={() => toggleCallback()}
      >
        <>
          <Styled.QuickviewImage>
            <>
              {!isEmpty(displayImage?.urlLarge) && (
                <img
                  data-test='displayImage'
                  src={displayImage?.urlLarge}
                  ref={imageRef}
                  alt={`@${displayImage?.author}`}
                />
              )}
            </>
          </Styled.QuickviewImage>
        </>
        <div ref={tabsRef}>
          <Tabs activeTabFromProp={activeTab} toggleTabCallback={(index) => goToTab(null, index)}>
            <Tab label={productTab}>
              {displayError ? (
                <Styled.OopsMessage data-test='oops-message'>
                  <p>Oops there are currently no products to show</p>
                  <p>
                    <Button kind='secondary' onClick={(event) => goToTab(event, 1)}>
                      Shop products for similar ideas
                    </Button>
                  </p>
                </Styled.OopsMessage>
              ) : (
                <>
                  {!isLoading &&
                    productsData.products.map((product, index) => {
                      const { id } = product
                      const { name } = product.attributes
                      const reviews = productsData.included.find(
                        (inc) => inc.type === 'reviewstatistics' && inc.id === product.id
                      )
                      const prices = productsData.included.find((inc) => inc.type === 'prices' && inc.id === product.id)
                      const { attributes: price } = prices

                      const reviewRating =
                        reviews.attributes && reviews.attributes.avgRating !== 0 ? reviews.attributes.avgRating : null
                      return (
                        <Styled.ProductContainer key={index}>
                          <ProductPanel
                            partNumber={id}
                            onClick={() => productPanelOnClick(product.id)}
                            name={name}
                            price={price.now}
                            wasPrice={price.was}
                            flashText={price.flashText}
                            showBorder={index !== 0}
                            rating={reviewRating}
                            ratingCount={reviews.attributes && reviews.attributes.reviewCount}
                            key={`prod-${product}-${index}`}
                            moreDetailsButton
                          />
                        </Styled.ProductContainer>
                      )
                    })}
                  {displayImage?.nonPDPs?.map((panel, index) => (
                    <Styled.ProductContainer key={index}>
                      <PLPPanel
                        key={`panel-${index}`}
                        dataTest='PLP-view'
                        link={panel.link}
                        imageUrl={panel.images[0].large.link}
                        name={panel.name}
                      />
                    </Styled.ProductContainer>
                  ))}
                </>
              )}
            </Tab>
            <Tab label='See other ideas'>
              <Styled.GalleryContainer>
                {galleryImages.length > 0 &&
                  galleryImages.map(
                    (image, index) =>
                      !isEmpty(displayImage) &&
                      image.url !== displayImage?.url &&
                      index <= galleryImagesLimit && (
                        <Styled.ImageItem type='mini' key={`${image.url}-mini`} data-test={`mini-image-${index}`}>
                          <Image
                            src={image.url}
                            alt={`@${image && image.author}`}
                            lazy
                            onClick={() => {
                              updateDrawerInformation(image)
                              setActiveTab(0)
                            }}
                          />
                        </Styled.ImageItem>
                      )
                  )}
              </Styled.GalleryContainer>
            </Tab>
          </Tabs>
        </div>
      </Drawer>
    </>
  )
}

UGCQuickView.displayName = 'UGCQuickView'

UGCQuickView.propTypes = {
  displayImage: PropTypes.object,
  galleryImages: PropTypes.array,
  toggleCallback: PropTypes.func,
  updateDrawerInformation: PropTypes.func,
  productTab: PropTypes.string
}

UGCQuickView.defaultProps = {
  displayImage: {},
  galleryImages: [],
  galleryImagesLimit: 12,
  productTab: 'Shop these products'
}

export default UGCQuickView
